import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtEur, fmtPct, fmtDate, MESI } from "@/lib/format";
import { TrendingUp, TrendingDown, Calendar, Receipt, Database, Loader2, AlertTriangle, CalendarClock } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart, Cell } from "recharts";
import { toast } from "sonner";
import { PeriodPicker, usePeriod } from "@/components/PeriodPicker";

const KpiCard = ({ label, value, sub, icon: Icon, positive, testid }) => (
  <div data-testid={testid} className="relative overflow-hidden bg-gradient-to-br from-[#121824] to-[#172030] border border-[#222D42] rounded-xl p-5 shadow-md">
    <div className="flex items-start justify-between">
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</p>
        <p className={`mt-2 font-mono font-bold text-2xl md:text-3xl tracking-tight ${positive === true ? "text-emerald-400" : positive === false ? "text-red-400" : "text-slate-50"}`}>
          {value}
        </p>
        {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
      </div>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${positive === true ? "bg-emerald-500/10 text-emerald-400" : positive === false ? "bg-red-500/10 text-red-400" : "bg-amber-500/10 text-amber-400"}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
  </div>
);

export default function Dashboard() {
  const { period, setPeriod, label: periodLabel } = usePeriod();
  const year = period.year;
  const [data, setData] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([api.dashboard(period.year, period.month), api.listVehicles(), api.listReports()])
      .then(([d, vs, rs]) => { setData(d); setVehicles(vs || []); setReports(rs || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, [period.year, period.month]); // eslint-disable-line react-hooks/exhaustive-deps

  const doSeed = async () => {
    setSeeding(true);
    try {
      await api.seed();
      toast.success("Dati demo caricati");
      load();
    } catch { toast.error("Errore nel caricamento demo"); }
    setSeeding(false);
  };

  if (!data) {
    return <div className="flex items-center justify-center h-64 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Caricamento…</div>;
  }

  const monthly = data.monthly.map((m, i) => ({ ...m, name: MESI[i] }));
  const empty = data.events_count === 0 && data.total_fixed_expenses === 0;

  const todayStr = new Date().toISOString().slice(0, 10);
  const in30 = new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10);
  const alerts = [];
  vehicles.forEach((v) => (v.deadlines || []).forEach((dl) => { if (dl.date && dl.date <= in30) alerts.push({ vehicle: v.name, label: dl.label, date: dl.date, overdue: dl.date < todayStr }); }));
  alerts.sort((a, b) => a.date.localeCompare(b.date));
  const overdueCount = alerts.filter((a) => a.overdue).length;
  const guasti = reports.filter((r) => (r.materiale_guasto || "").trim() || (r.danni_veicolo || "").trim());

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-head text-3xl md:text-4xl font-bold tracking-tight text-slate-100">Cruscotto</h1>
          <p data-testid="dashboard-period-caption" className="text-slate-500 text-sm mt-1 flex items-center gap-2">{periodLabel} · profitti e perdite · Betti SRL{loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}</p>
        </div>
        <PeriodPicker period={period} setPeriod={setPeriod} testPrefix="dashboard-period" />
      </div>

      {empty && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-amber-400 font-medium">Nessun dato per questo periodo</p>
            <p className="text-slate-400 text-sm mt-1">Carica dei dati dimostrativi per vedere subito come funziona, oppure inizia aggiungendo i tuoi eventi e le spese.</p>
          </div>
          <button data-testid="seed-button" onClick={doSeed} disabled={seeding} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-4 py-2 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-60">
            {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />} Carica dati demo
          </button>
        </div>
      )}

      {alerts.length > 0 && (
        <div data-testid="deadline-alerts" className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
          <h3 className="font-head text-sm font-semibold text-red-400 flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4" /> Scadenze veicoli{overdueCount > 0 ? ` · ${overdueCount} scadut${overdueCount === 1 ? "a" : "e"}` : ""} (prossimi 30 giorni)</h3>
          <div className="flex flex-wrap gap-2">
            {alerts.map((a, i) => (
              <span key={i} className={`text-xs px-2.5 py-1 rounded-full border inline-flex items-center gap-1.5 ${a.overdue ? "bg-red-500/10 text-red-400 border-red-500/30" : "bg-amber-500/10 text-amber-400 border-amber-500/20"}`}>
                <CalendarClock className="w-3.5 h-3.5" /> {a.vehicle} · {a.label} · {fmtDate(a.date)}{a.overdue ? " (scaduta)" : ""}
              </span>
            ))}
          </div>
        </div>
      )}

      {guasti.length > 0 && (
        <div data-testid="guasti-alerts" className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
          <h3 className="font-head text-sm font-semibold text-red-400 flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4" /> Guasti segnalati nei report ({guasti.length})</h3>
          <div className="space-y-1.5">
            {guasti.map((r, i) => (
              <div key={i} className="text-xs text-slate-300 flex flex-wrap items-center gap-2">
                <span className="text-amber-400 font-medium">{r.title || "Report"}</span>
                <span className="text-slate-600">{fmtDate(r.event_date)}</span>
                {(r.materiale_guasto || "").trim() && <span className="px-2 py-0.5 rounded-full bg-red-500/10 border border-red-500/20">Materiale: {r.materiale_guasto}</span>}
                {(r.danni_veicolo || "").trim() && <span className="px-2 py-0.5 rounded-full bg-red-500/10 border border-red-500/20">Veicolo: {r.danni_veicolo}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
        <KpiCard testid="kpi-revenue" label="Ricavi Reali" value={fmtEur(data.contab_revenue ?? 0)} sub="fatture incassate" icon={TrendingUp} />
        <KpiCard testid="kpi-costs" label="Costi Reali" value={fmtEur(data.contab_costs ?? 0)} sub="spese contabilizzate" icon={Receipt} />
        <KpiCard testid="kpi-profit" label="Utile Netto Reale" value={fmtEur(data.contab_net_profit ?? 0)} sub={fmtPct(data.contab_margin_pct ?? 0) + " margine"} icon={(data.contab_net_profit ?? 0) >= 0 ? TrendingUp : TrendingDown} positive={(data.contab_net_profit ?? 0) >= 0} />
      </div>

      <div data-testid="event-result-section" className="bg-[#121824] border border-[#222D42] rounded-xl p-5">
        <h3 className="font-head text-lg font-semibold text-slate-200 mb-1 flex items-center gap-2"><Calendar className="w-4 h-4 text-amber-400" /> Risultato dagli eventi</h3>
        <p className="text-[11px] text-slate-600 mb-4">Solo ricavi e costi degli eventi (dai report quando disponibili), senza i costi fissi di struttura.</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-[#1A2335] rounded-lg p-4"><p className="text-[10px] uppercase text-slate-500">Ricavi da eventi</p><p data-testid="ev-revenue" className="font-mono text-xl font-bold text-slate-100 mt-1">{fmtEur(data.total_revenue)}</p></div>
          <div className="bg-[#1A2335] rounded-lg p-4"><p className="text-[10px] uppercase text-slate-500">Costi da eventi</p><p data-testid="ev-costs" className="font-mono text-xl font-bold text-red-400 mt-1">{fmtEur(data.real_event_costs ?? data.total_event_costs)}</p></div>
          <div className="bg-[#1A2335] rounded-lg p-4"><p className="text-[10px] uppercase text-slate-500">Utile da eventi</p><p data-testid="ev-profit" className={`font-mono text-xl font-bold mt-1 ${(data.event_profit_real ?? 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>{fmtEur(data.event_profit_real ?? (data.total_revenue - (data.real_event_costs ?? data.total_event_costs)))}</p></div>
          <div className="bg-[#1A2335] rounded-lg p-4"><p className="text-[10px] uppercase text-slate-500">Margine medio / evento</p><p data-testid="ev-avg-margin" className={`font-mono text-xl font-bold mt-1 ${(data.avg_event_margin_real ?? 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>{fmtEur(data.avg_event_margin_real ?? data.avg_event_margin)}</p></div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        <div className="lg:col-span-2 bg-[#121824] border border-[#222D42] rounded-xl p-5">
          <h3 className="font-head text-lg font-semibold text-slate-200 mb-4">Ricavi vs Costi (mensile {year})</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={monthly} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="#222D42" vertical={false} />
              <XAxis dataKey="name" stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `${v / 1000}k`} />
              <Tooltip contentStyle={{ background: "#121824", border: "1px solid #222D42", borderRadius: 12, color: "#F1F5F9" }} formatter={(v) => fmtEur(v)} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="ricavi" name="Ricavi" fill="#E5A93C" radius={[4, 4, 0, 0]} />
              <Bar dataKey="costi" name="Costi" fill="#475569" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-[#121824] border border-[#222D42] rounded-xl p-5">
          <h3 className="font-head text-lg font-semibold text-slate-200 mb-4">Utile mensile {year}</h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={monthly}>
              <defs>
                <linearGradient id="profitGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#222D42" vertical={false} />
              <XAxis dataKey="name" stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v / 1000}k`} />
              <Tooltip contentStyle={{ background: "#121824", border: "1px solid #222D42", borderRadius: 12, color: "#F1F5F9" }} formatter={(v) => fmtEur(v)} />
              <Area type="monotone" dataKey="utile" name="Utile" stroke="#10B981" strokeWidth={2} fill="url(#profitGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <div className="bg-[#121824] border border-[#222D42] rounded-xl p-5">
          <h3 className="font-head text-lg font-semibold text-slate-200 mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-400" /> Eventi più redditizi</h3>
          <div className="space-y-2">
            {data.top_events.length === 0 && <p className="text-slate-500 text-sm">Nessun evento.</p>}
            {data.top_events.map((e) => {
              const showMargin = e.has_real ? e.real_margin : e.margin;
              const showPct = e.has_real ? e.real_margin_pct : e.margin_pct;
              return (
                <div key={e.id} className={`flex items-center justify-between p-3 rounded-lg ${e.has_real ? "bg-amber-500/5 border border-amber-500/20" : "bg-[#1A2335] border border-dashed border-[#2B3850]"}`}>
                  <div className="min-w-0">
                    <p className="text-slate-200 text-sm font-medium truncate">{e.name}</p>
                    <div className="flex items-center gap-2">
                      <p className="text-slate-500 text-xs truncate">{e.client}</p>
                      <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full shrink-0 ${e.has_real ? "bg-amber-500/15 text-amber-300" : "bg-sky-500/10 text-sky-300"}`}>{e.has_real ? "Reale" : "Stimato"}</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <p className={`font-mono text-sm font-semibold ${showMargin >= 0 ? (e.has_real ? "text-amber-300" : "text-emerald-400") : "text-red-400"}`}>{fmtEur(showMargin)}</p>
                    <p className="text-slate-500 text-xs font-mono">{fmtPct(showPct)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-[#121824] border border-[#222D42] rounded-xl p-5">
          <h3 className="font-head text-lg font-semibold text-slate-200 mb-4 flex items-center gap-2"><Receipt className="w-4 h-4 text-amber-400" /> Spese per categoria</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.expense_categories} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#222D42" horizontal={false} />
              <XAxis type="number" stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v / 1000}k`} />
              <YAxis type="category" dataKey="category" stroke="#94A3B8" fontSize={11} width={90} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: "#121824", border: "1px solid #222D42", borderRadius: 12, color: "#F1F5F9" }} formatter={(v) => fmtEur(v)} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="amount" name="Spesa" radius={[0, 4, 4, 0]}>
                {data.expense_categories.map((_, i) => <Cell key={i} fill="#E5A93C" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          {data.expense_categories.length === 0 && <p className="text-slate-500 text-sm">Nessuna spesa registrata.</p>}
        </div>
      </div>
    </div>
  );
}
